<script lang="ts">
    import type {ComponentSize, Px} from '../types';
    import type {AutocompleteController} from './utils';
    import {afterUpdate, onDestroy, onMount} from 'svelte';
    import {pipe} from 'core/utils';
    import {strParams} from '../../utils/pipes';
    import {concatWith, debounceTime, take, skip} from 'rxjs/operators';
    import {getLocalization} from 'core/svelte-context/context';
    import {exists} from 'shared/utils/custom-utils';
    import {createFloatingActions} from 'svelte-floating-ui';
    import {floatingUiCommonSettings} from 'shared/utils/floating-ui-common-settings';
    import DefaultAutocompleteOptionText from './DefaultAutocompleteOptionText.svelte';

    export let autocompleteController: AutocompleteController;
    export let size: ComponentSize = 'md';
    export let topOffset: Px | null = null;
    export let anchorElement: HTMLElement;

    const localize = getLocalization();

    const state$ = autocompleteController.getState$();
    const debouncedState$ = state$.pipe(take(1), concatWith(state$.pipe(skip(1), debounceTime(100))));

    let resultListRef: HTMLUListElement;

    let floatingUiUpdateInterval: number | null = null;
    const [floatingRef, floatingContent, updateFloatingPosition] = createFloatingActions(floatingUiCommonSettings('bottom-start', 2));

    onMount(() => {
        floatingUiUpdateInterval = window.setInterval(() => {
            updateFloatingPosition();
        }, 250);
    });

    afterUpdate(() => {
        if (!resultListRef) {
            return
        }
        autocompleteController.core.checkSelectedResultVisible(resultListRef);
    });

    onDestroy(() => {
        if (exists(floatingUiUpdateInterval)) {
            window.clearInterval(floatingUiUpdateInterval);
        }
    });

    floatingRef(anchorElement);
</script>

<!-- svelte-ignore a11y-click-events-have-key-events keyboard events are caught elsewhere -->
<ul bind:this={resultListRef}
    role="listbox"
    id={autocompleteController.resultListId}
    class="dropdown-menu size-{size}"
    style="{topOffset ? `top: ${topOffset}` : ''}"
    use:floatingContent
    class:opened={$state$.expanded}
    on:mousedown={autocompleteController.core.handleResultMouseDown}
    on:click={autocompleteController.core.handleResultClick}>

    {#each $state$.results as result, index (autocompleteController.getResultValue(result))}
        <li role="option"
            id="autocomplete-result-{index}"
            class="autocomplete-result"

            tabindex="-1"
            aria-selected="{$state$.selectedIndex === index ? 'true' : 'false'}"
            aria-posinset="{index + 1}"
            aria-setsize="{$state$.results.length}"
            data-result-index="{index}">

            <div class="dropdown-menu-option">
                <DefaultAutocompleteOptionText optionText={autocompleteController.getResultValue(result)}
                                               queryText={$state$.inputValue}/>
            </div>
        </li>
    {/each}
</ul>

<div role="status" class="autocomplete-status sr-only" aria-live="polite">
    {#if $debouncedState$.loading}
        {localize(/* @kp-localization commons.LoadingInProgress */ 'commons.LoadingInProgress')}
    {:else if ($debouncedState$.results.length > 0)}
        {pipe(localize(/* @kp-localization commons.XAutosuggestOptionsDisplayed */ 'commons.XAutosuggestOptionsDisplayed'),
            strParams($debouncedState$.results.length.toString()))}
    {:else}
        {localize(/* @kp-localization commons.NoAutosuggestOptions */ 'commons.NoAutosuggestOptions')}
    {/if}
</div>

<style lang="less">
    @import (reference) "bootstrap-less/bootstrap/variables";
    @import (reference) "styles/partials/dropdowns";

    .autocomplete-result:hover,
    .autocomplete-result:focus,
    .autocomplete-result[aria-selected="true"] {
        & > .dropdown-menu-option {
            .dropdown-menu-option.selected-state();
        }
    }
</style>